import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  FolderKanban,
  Plus,
  Calendar,
  Users,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  MoreVertical,
  Edit,
  Trash2,
  ChevronRight,
  Target,
  DollarSign,
  Upload,
  Download,
  UserPlus,
  X,
  ListTodo,
  Milestone,
  ClipboardList,
  Send,
  Eye,
  MessageSquare,
  Sun,
  Moon,
  CloudSun,
} from "lucide-react";

const STATUS_COLORS = {
  planning: "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  on_hold: "bg-orange-100 text-orange-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const PRIORITY_COLORS = {
  low: "bg-gray-100 text-gray-600",
  medium: "bg-blue-100 text-blue-600",
  high: "bg-orange-100 text-orange-600",
  urgent: "bg-red-100 text-red-600",
};

const TASK_STATUS_COLORS = {
  todo: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  review: "bg-purple-100 text-purple-700",
  done: "bg-green-100 text-green-700",
};

const ProjectManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  
  // Check if user is constructor (limited access)
  const isConstructor = user?.role === "constructor";
  const canManageProjects = user?.role === "admin" || user?.role === "project_manager";
  
  // Dialogs
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewMilestone, setShowNewMilestone] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showProgressUpdate, setShowProgressUpdate] = useState(false);
  const [selectedTaskForProgress, setSelectedTaskForProgress] = useState(null);
  const [progressNote, setProgressNote] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  
  // Form states
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    project_type: "development",
    priority: "medium",
    start_date: "",
    target_end_date: "",
    budget: 0,
    location: "",
  });
  
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    assigned_to: "",
    due_date: "",
    milestone_id: "",
  });
  
  const [newMilestone, setNewMilestone] = useState({
    name: "",
    description: "",
    target_date: "",
  });
  
  const [availableMembers, setAvailableMembers] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Progress Reports
  const [showNewReport, setShowNewReport] = useState(false);
  const [showViewReport, setShowViewReport] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [projectReports, setProjectReports] = useState([]);
  const [newReport, setNewReport] = useState({
    report_date: new Date().toISOString().split('T')[0],
    shift_type: "day",
    hours_worked: 8,
    work_summary: "",
    progress_percent: 0,
    materials_used: "",
    issues_encountered: "",
    next_steps: "",
    weather_conditions: "",
    photos: [],
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/projects/dashboard/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
      setSelectedProject(response.data);
    } catch (error) {
      toast.error("Failed to load project details");
    }
  };

  const fetchAvailableMembers = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/available-members`, { withCredentials: true });
      setAvailableMembers(response.data);
    } catch (error) {
      console.error("Failed to fetch available members:", error);
    }
  };

  const fetchProjectReports = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/reports`, { withCredentials: true });
      setProjectReports(response.data);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    }
  };

  const handleCreateReport = async () => {
    if (!newReport.work_summary.trim()) {
      toast.error("Work summary is required");
      return;
    }
    
    try {
      console.log("Submitting report:", {
        ...newReport,
        project_id: selectedProject.project_id,
      });
      const response = await axios.post(`${API}/progress-reports`, {
        ...newReport,
        project_id: selectedProject.project_id,
      }, { withCredentials: true });
      console.log("Report response:", response.data);
      toast.success("Progress report submitted!");
      setShowNewReport(false);
      setNewReport({
        report_date: new Date().toISOString().split('T')[0],
        shift_type: "day",
        hours_worked: 8,
        work_summary: "",
        progress_percent: 0,
        materials_used: "",
        issues_encountered: "",
        next_steps: "",
        weather_conditions: "",
        photos: [],
      });
      fetchProjectReports(selectedProject.project_id);
    } catch (error) {
      console.error("Report submission error:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Failed to submit report");
    }
  };

  const handleReviewReport = async (reportId, status, comments = "") => {
    try {
      await axios.put(`${API}/progress-reports/${reportId}/review`, {
        status,
        comments
      }, { withCredentials: true });
      toast.success(`Report ${status}`);
      fetchProjectReports(selectedProject.project_id);
      setShowViewReport(false);
      setSelectedReport(null);
    } catch (error) {
      toast.error("Failed to review report");
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setNewReport(prev => ({
        ...prev,
        photos: [...prev.photos, response.data.url]
      }));
      toast.success("Photo uploaded");
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    
    try {
      await axios.post(`${API}/projects`, newProject, { withCredentials: true });
      toast.success("Project created successfully!");
      setShowNewProject(false);
      setNewProject({
        name: "",
        description: "",
        project_type: "development",
        priority: "medium",
        start_date: "",
        target_end_date: "",
        budget: 0,
        location: "",
      });
      fetchProjects();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
    }
  };

  const handleUpdateProjectStatus = async (projectId, status) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { status }, { withCredentials: true });
      toast.success("Project status updated");
      fetchProjects();
      if (selectedProject?.project_id === projectId) {
        fetchProjectDetails(projectId);
      }
    } catch (error) {
      toast.error("Failed to update project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Delete this project and all its tasks? This cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      toast.success("Project deleted");
      setSelectedProject(null);
      fetchProjects();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error("Task title is required");
      return;
    }
    
    try {
      await axios.post(`${API}/tasks`, {
        ...newTask,
        project_id: selectedProject.project_id,
      }, { withCredentials: true });
      toast.success("Task created!");
      setShowNewTask(false);
      setNewTask({
        title: "",
        description: "",
        priority: "medium",
        assigned_to: "",
        due_date: "",
        milestone_id: "",
      });
      fetchProjectDetails(selectedProject.project_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create task");
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, updates, { withCredentials: true });
      toast.success("Task updated");
      fetchProjectDetails(selectedProject.project_id);
      setEditingTask(null);
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      toast.success("Task deleted");
      fetchProjectDetails(selectedProject.project_id);
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleCreateMilestone = async () => {
    if (!newMilestone.name.trim()) {
      toast.error("Milestone name is required");
      return;
    }
    
    try {
      await axios.post(`${API}/projects/${selectedProject.project_id}/milestones`, {
        ...newMilestone,
        project_id: selectedProject.project_id,
      }, { withCredentials: true });
      toast.success("Milestone created!");
      setShowNewMilestone(false);
      setNewMilestone({ name: "", description: "", target_date: "" });
      fetchProjectDetails(selectedProject.project_id);
    } catch (error) {
      toast.error("Failed to create milestone");
    }
  };

  const handleUpdateMilestone = async (milestoneId, status) => {
    try {
      await axios.put(`${API}/milestones/${milestoneId}?status=${status}`, {}, { withCredentials: true });
      toast.success("Milestone updated");
      fetchProjectDetails(selectedProject.project_id);
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const handleAddTeamMember = async (userId) => {
    try {
      await axios.post(`${API}/projects/${selectedProject.project_id}/team/${userId}`, {}, { withCredentials: true });
      toast.success("Team member added");
      fetchProjectDetails(selectedProject.project_id);
      setShowAddTeamMember(false);
    } catch (error) {
      toast.error("Failed to add team member");
    }
  };

  const handleRemoveTeamMember = async (userId) => {
    try {
      await axios.delete(`${API}/projects/${selectedProject.project_id}/team/${userId}`, { withCredentials: true });
      toast.success("Team member removed");
      fetchProjectDetails(selectedProject.project_id);
    } catch (error) {
      toast.error("Failed to remove team member");
    }
  };

  const handleAddProgressUpdate = async () => {
    if (!selectedTaskForProgress) return;
    if (!progressNote.trim() && progressPercent === (selectedTaskForProgress.progress_percent || 0)) {
      toast.error("Add a note or change the progress percentage");
      return;
    }
    
    try {
      await axios.post(`${API}/tasks/${selectedTaskForProgress.task_id}/progress`, {
        note: progressNote,
        progress_percent: progressPercent
      }, { withCredentials: true });
      toast.success("Progress updated!");
      setShowProgressUpdate(false);
      setSelectedTaskForProgress(null);
      setProgressNote("");
      setProgressPercent(0);
      fetchProjectDetails(selectedProject.project_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update progress");
    }
  };

  const openProgressDialog = (task) => {
    setSelectedTaskForProgress(task);
    setProgressPercent(task.progress_percent || 0);
    setProgressNote("");
    setShowProgressUpdate(true);
  };

  const handleDocumentUpload = async (file) => {
    if (!file) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const fileType = file.type.includes("pdf") ? "pdf" 
        : file.type.includes("image") ? "image"
        : file.type.includes("doc") ? "doc"
        : file.type.includes("sheet") ? "spreadsheet"
        : "other";
      
      await axios.post(`${API}/projects/${selectedProject.project_id}/documents`, {
        project_id: selectedProject.project_id,
        name: file.name,
        file_url: uploadResponse.data.url,
        file_type: fileType,
        category: "general",
      }, { withCredentials: true });
      
      toast.success("Document uploaded!");
      fetchProjectDetails(selectedProject.project_id);
      setShowUploadDoc(false);
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      await axios.delete(`${API}/documents/${documentId}`, { withCredentials: true });
      toast.success("Document deleted");
      fetchProjectDetails(selectedProject.project_id);
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Project Detail View
  if (selectedProject) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedProject(null)}>
              ← Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{selectedProject.name}</h1>
              <p className="text-gray-500">{selectedProject.location || "No location set"}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[selectedProject.status]}`}>
              {selectedProject.status?.replace("_", " ")}
            </span>
          </div>
          {canManageProjects && (
            <div className="flex gap-2">
              <select
                value={selectedProject.status}
                onChange={(e) => handleUpdateProjectStatus(selectedProject.project_id, e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="planning">Planning</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <Button variant="destructive" size="sm" onClick={() => handleDeleteProject(selectedProject.project_id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex justify-between text-sm mb-2">
            <span>Progress</span>
            <span className="font-medium">{selectedProject.progress || 0}%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${selectedProject.progress || 0}%` }}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex gap-4 overflow-x-auto">
            {(isConstructor 
              ? ["overview", "tasks", "reports", "documents"]  // Constructor sees reports
              : ["overview", "tasks", "reports", "milestones", "team", "documents"]
            ).map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === "reports") {
                    fetchProjectReports(selectedProject.project_id);
                  }
                }}
                className={`px-4 py-2 border-b-2 transition-colors capitalize whitespace-nowrap ${
                  activeTab === tab
                    ? "border-primary text-primary font-medium"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg border p-6">
          {activeTab === "overview" && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-2">Description</h3>
                <p className="text-gray-600">{selectedProject.description || "No description"}</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Timeline</p>
                    <p className="font-medium">
                      {selectedProject.start_date || "TBD"} → {selectedProject.target_end_date || "TBD"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Budget</p>
                    <p className="font-medium">
                      K{(selectedProject.spent || 0).toLocaleString()} / K{(selectedProject.budget || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Team</p>
                    <p className="font-medium">{selectedProject.team?.length || 0} members</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "tasks" && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="font-medium">
                  {isConstructor ? "My Tasks" : "Tasks"} ({selectedProject.tasks?.length || 0})
                </h3>
                {canManageProjects && (
                  <Button size="sm" onClick={() => setShowNewTask(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Task
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {selectedProject.tasks?.map((task) => (
                  <div key={task.task_id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <button
                          onClick={() => handleUpdateTask(task.task_id, { 
                            status: task.status === "done" ? "todo" : "done" 
                          })}
                          className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            task.status === "done" ? "bg-green-500 border-green-500 text-white" : "border-gray-300"
                          }`}
                        >
                          {task.status === "done" && <CheckCircle2 className="w-3 h-3" />}
                        </button>
                        <div className="flex-1">
                          <p className={`font-medium ${task.status === "done" ? "line-through text-gray-400" : ""}`}>
                            {task.title}
                          </p>
                          {task.description && (
                            <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                          )}
                          
                          {/* Progress Bar */}
                          <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-500">Progress</span>
                              <span className="font-medium">{task.progress_percent || 0}%</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary transition-all" 
                                style={{ width: `${task.progress_percent || 0}%` }} 
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${TASK_STATUS_COLORS[task.status]}`}>
                              {task.status?.replace("_", " ")}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${PRIORITY_COLORS[task.priority]}`}>
                              {task.priority}
                            </span>
                            {task.due_date && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {task.due_date}
                              </span>
                            )}
                            {task.assignee && (
                              <span className="text-xs text-gray-500">
                                → {task.assignee.name}
                              </span>
                            )}
                          </div>
                          
                          {/* Progress Notes */}
                          {task.progress_notes && task.progress_notes.length > 0 && (
                            <div className="mt-3 border-t pt-2">
                              <p className="text-xs font-medium text-gray-500 mb-1">Recent Updates:</p>
                              <div className="space-y-1 max-h-24 overflow-y-auto">
                                {task.progress_notes.slice(-3).reverse().map((note, idx) => (
                                  <div key={idx} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                    <span className="font-medium">{note.by}</span>
                                    <span className="text-gray-400 mx-1">•</span>
                                    <span className="text-gray-400">{new Date(note.date).toLocaleDateString()}</span>
                                    <p className="mt-0.5">{note.note}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0 ml-2">
                        {/* Update Progress Button - available to assignee or managers */}
                        {(task.assigned_to === user?.user_id || canManageProjects) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openProgressDialog(task)}
                            className="text-xs"
                          >
                            Update
                          </Button>
                        )}
                        {canManageProjects && (
                          <>
                            <select
                              value={task.status}
                              onChange={(e) => handleUpdateTask(task.task_id, { status: e.target.value })}
                              className="text-xs border rounded px-2 py-1"
                            >
                              <option value="todo">To Do</option>
                              <option value="in_progress">In Progress</option>
                              <option value="review">Review</option>
                              <option value="done">Done</option>
                            </select>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTask(task.task_id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(!selectedProject.tasks || selectedProject.tasks.length === 0) && (
                  <p className="text-center text-gray-500 py-8">
                    {isConstructor ? "No tasks assigned to you yet." : "No tasks yet. Create one to get started!"}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === "reports" && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="font-medium">Progress Reports</h3>
                {isConstructor && (
                  <Button size="sm" onClick={() => setShowNewReport(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Submit Report
                  </Button>
                )}
              </div>
              
              <div className="space-y-3">
                {projectReports.map((report) => (
                  <div 
                    key={report.report_id} 
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setSelectedReport(report);
                      setShowViewReport(true);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">{report.report_date}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            report.shift_type === "day" ? "bg-yellow-100 text-yellow-700" :
                            report.shift_type === "night" ? "bg-indigo-100 text-indigo-700" :
                            "bg-blue-100 text-blue-700"
                          }`}>
                            {report.shift_type === "day" && <Sun className="w-3 h-3 inline mr-1" />}
                            {report.shift_type === "night" && <Moon className="w-3 h-3 inline mr-1" />}
                            {report.shift_type === "full_day" && <CloudSun className="w-3 h-3 inline mr-1" />}
                            {report.shift_type}
                          </span>
                          <span className="text-sm text-gray-500">{report.hours_worked}h worked</span>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">{report.work_summary}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span>By: {report.submitted_by_name}</span>
                          <span>Progress: {report.progress_percent}%</span>
                          {report.photos?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" /> {report.photos.length} photos
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          report.status === "submitted" ? "bg-yellow-100 text-yellow-700" :
                          report.status === "reviewed" ? "bg-blue-100 text-blue-700" :
                          "bg-green-100 text-green-700"
                        }`}>
                          {report.status}
                        </span>
                        <Eye className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
                
                {projectReports.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No progress reports yet.</p>
                    {isConstructor && (
                      <Button variant="outline" className="mt-4" onClick={() => setShowNewReport(true)}>
                        Submit your first report
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "milestones" && canManageProjects && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="font-medium">Milestones</h3>
                <Button size="sm" onClick={() => setShowNewMilestone(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Milestone
                </Button>
              </div>
              <div className="space-y-4">
                {selectedProject.milestones?.map((milestone, index) => (
                  <div key={milestone.milestone_id} className="flex items-center gap-4">
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleUpdateMilestone(
                          milestone.milestone_id,
                          milestone.status === "completed" ? "pending" : "completed"
                        )}
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          milestone.status === "completed"
                            ? "bg-green-500 text-white"
                            : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {milestone.status === "completed" ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </button>
                      {index < (selectedProject.milestones?.length || 0) - 1 && (
                        <div className="w-0.5 h-8 bg-gray-200 mt-1" />
                      )}
                    </div>
                    <div className="flex-1 border rounded-lg p-4">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">{milestone.name}</p>
                          {milestone.description && (
                            <p className="text-sm text-gray-500">{milestone.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          {milestone.target_date && (
                            <p className="text-sm text-gray-500">Target: {milestone.target_date}</p>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            milestone.status === "completed" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                          }`}>
                            {milestone.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {(!selectedProject.milestones || selectedProject.milestones.length === 0) && (
                  <p className="text-center text-gray-500 py-8">No milestones yet.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === "team" && canManageProjects && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="font-medium">Team Members</h3>
                <Button size="sm" onClick={() => {
                  fetchAvailableMembers(selectedProject.project_id);
                  setShowAddTeamMember(true);
                }}>
                  <UserPlus className="w-4 h-4 mr-1" /> Add Member
                </Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedProject.team?.map((member) => (
                  <div key={member.user_id} className="border rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {member.picture ? (
                        <img src={member.picture} alt={member.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                          {member.name?.[0]}
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveTeamMember(member.user_id)}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {(!selectedProject.team || selectedProject.team.length === 0) && (
                  <p className="text-gray-500 col-span-full text-center py-8">No team members assigned.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === "documents" && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="font-medium">Documents</h3>
                <Button size="sm" onClick={() => setShowUploadDoc(true)}>
                  <Upload className="w-4 h-4 mr-1" /> Upload
                </Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedProject.documents?.map((doc) => (
                  <div key={doc.document_id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-gray-400" />
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          <p className="text-xs text-gray-500">{doc.category}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Download className="w-4 h-4 text-gray-500" />
                        </a>
                        <button
                          onClick={() => handleDeleteDocument(doc.document_id)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {(!selectedProject.documents || selectedProject.documents.length === 0) && (
                  <p className="text-gray-500 col-span-full text-center py-8">No documents uploaded.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* New Task Dialog */}
        <Dialog open={showNewTask} onOpenChange={setShowNewTask}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Title *</label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Task title"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                  rows={3}
                  placeholder="Task description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                    className="w-full border rounded-md p-2 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Due Date</label>
                  <Input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Assign To</label>
                <select
                  value={newTask.assigned_to}
                  onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                >
                  <option value="">Unassigned</option>
                  {selectedProject.team?.map((member) => (
                    <option key={member.user_id} value={member.user_id}>{member.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewTask(false)}>Cancel</Button>
                <Button onClick={handleCreateTask}>Create Task</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* New Milestone Dialog */}
        <Dialog open={showNewMilestone} onOpenChange={setShowNewMilestone}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Milestone</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input
                  value={newMilestone.name}
                  onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                  placeholder="Milestone name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={newMilestone.description}
                  onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Target Date</label>
                <Input
                  type="date"
                  value={newMilestone.target_date}
                  onChange={(e) => setNewMilestone({ ...newMilestone, target_date: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewMilestone(false)}>Cancel</Button>
                <Button onClick={handleCreateMilestone}>Create Milestone</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Team Member Dialog */}
        <Dialog open={showAddTeamMember} onOpenChange={setShowAddTeamMember}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availableMembers
                .filter((m) => !selectedProject.team?.find((t) => t.user_id === m.user_id))
                .map((member) => (
                  <div
                    key={member.user_id}
                    onClick={() => handleAddTeamMember(member.user_id)}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    {member.picture ? (
                      <img src={member.picture} alt={member.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-medium">
                        {member.name?.[0]}
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-xs text-gray-500">{member.role}</p>
                    </div>
                  </div>
                ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Document Dialog */}
        <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  id="doc-upload"
                  className="hidden"
                  onChange={(e) => handleDocumentUpload(e.target.files?.[0])}
                />
                <label htmlFor="doc-upload" className="cursor-pointer">
                  {uploadingDoc ? (
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, Images, Documents</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Progress Update Dialog */}
        <Dialog open={showProgressUpdate} onOpenChange={setShowProgressUpdate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Task Progress</DialogTitle>
            </DialogHeader>
            {selectedTaskForProgress && (
              <div className="space-y-4">
                <div>
                  <p className="font-medium">{selectedTaskForProgress.title}</p>
                  <p className="text-sm text-gray-500">{selectedTaskForProgress.description}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Progress ({progressPercent}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progressPercent}
                    onChange={(e) => setProgressPercent(parseInt(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Progress Note</label>
                  <textarea
                    value={progressNote}
                    onChange={(e) => setProgressNote(e.target.value)}
                    className="w-full border rounded-md p-2 text-sm mt-1"
                    rows={3}
                    placeholder="Describe what was done, any issues, or updates..."
                  />
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowProgressUpdate(false)}>Cancel</Button>
                  <Button onClick={handleAddProgressUpdate}>Submit Update</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* New Progress Report Dialog */}
        <Dialog open={showNewReport} onOpenChange={setShowNewReport}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Submit Progress Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Report Date *</label>
                  <Input
                    type="date"
                    value={newReport.report_date}
                    onChange={(e) => setNewReport({ ...newReport, report_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Shift Type</label>
                  <select
                    value={newReport.shift_type}
                    onChange={(e) => setNewReport({ ...newReport, shift_type: e.target.value })}
                    className="w-full border rounded-md p-2 text-sm"
                  >
                    <option value="day">Day Shift</option>
                    <option value="night">Night Shift</option>
                    <option value="full_day">Full Day</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Hours Worked</label>
                  <Input
                    type="number"
                    min="0"
                    max="24"
                    step="0.5"
                    value={newReport.hours_worked}
                    onChange={(e) => setNewReport({ ...newReport, hours_worked: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Work Summary *</label>
                <textarea
                  value={newReport.work_summary}
                  onChange={(e) => setNewReport({ ...newReport, work_summary: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                  rows={3}
                  placeholder="Describe what work was completed today..."
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Progress Estimate ({newReport.progress_percent}%)</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newReport.progress_percent}
                  onChange={(e) => setNewReport({ ...newReport, progress_percent: parseInt(e.target.value) })}
                  className="w-full mt-2"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Materials Used</label>
                  <textarea
                    value={newReport.materials_used}
                    onChange={(e) => setNewReport({ ...newReport, materials_used: e.target.value })}
                    className="w-full border rounded-md p-2 text-sm"
                    rows={2}
                    placeholder="List materials used..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Weather Conditions</label>
                  <Input
                    value={newReport.weather_conditions}
                    onChange={(e) => setNewReport({ ...newReport, weather_conditions: e.target.value })}
                    placeholder="e.g., Sunny, Rainy..."
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Issues Encountered</label>
                <textarea
                  value={newReport.issues_encountered}
                  onChange={(e) => setNewReport({ ...newReport, issues_encountered: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                  rows={2}
                  placeholder="Any problems or brokers..."
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Next Steps</label>
                <textarea
                  value={newReport.next_steps}
                  onChange={(e) => setNewReport({ ...newReport, next_steps: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                  rows={2}
                  placeholder="What's planned for next shift..."
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Photos</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {newReport.photos.map((url, idx) => (
                    <div key={idx} className="relative">
                      <img src={url} alt={`Photo ${idx + 1}`} className="w-20 h-20 object-cover rounded border" />
                      <button
                        onClick={() => setNewReport(prev => ({
                          ...prev,
                          photos: prev.photos.filter((_, i) => i !== idx)
                        }))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                    />
                    {uploadingPhoto ? (
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    ) : (
                      <Plus className="w-5 h-5 text-gray-400" />
                    )}
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowNewReport(false)}>Cancel</Button>
                <Button onClick={handleCreateReport}>
                  <Send className="w-4 h-4 mr-2" /> Submit Report
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog open={showViewReport} onOpenChange={setShowViewReport}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Progress Report - {selectedReport?.report_date}</DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <span className={`px-3 py-1 rounded text-sm font-medium ${
                    selectedReport.status === "submitted" ? "bg-yellow-100 text-yellow-700" :
                    selectedReport.status === "reviewed" ? "bg-blue-100 text-blue-700" :
                    "bg-green-100 text-green-700"
                  }`}>
                    {selectedReport.status}
                  </span>
                  <span className="text-sm text-gray-500">
                    {selectedReport.shift_type} shift • {selectedReport.hours_worked}h worked
                  </span>
                  <span className="text-sm text-gray-500">
                    By: {selectedReport.submitted_by_name}
                  </span>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Work Summary</h4>
                  <p className="text-gray-700">{selectedReport.work_summary}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Progress</h4>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary" 
                        style={{ width: `${selectedReport.progress_percent}%` }} 
                      />
                    </div>
                    <span className="font-medium">{selectedReport.progress_percent}%</span>
                  </div>
                </div>
                
                {selectedReport.materials_used && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Materials Used</h4>
                    <p className="text-gray-700">{selectedReport.materials_used}</p>
                  </div>
                )}
                
                {selectedReport.weather_conditions && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Weather</h4>
                    <p className="text-gray-700">{selectedReport.weather_conditions}</p>
                  </div>
                )}
                
                {selectedReport.issues_encountered && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Issues Encountered</h4>
                    <p className="text-gray-700 text-red-600">{selectedReport.issues_encountered}</p>
                  </div>
                )}
                
                {selectedReport.next_steps && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Next Steps</h4>
                    <p className="text-gray-700">{selectedReport.next_steps}</p>
                  </div>
                )}
                
                {selectedReport.photos?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Photos</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedReport.photos.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt={`Photo ${idx + 1}`} className="w-24 h-24 object-cover rounded border hover:opacity-80" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedReport.manager_comments && (
                  <div className="bg-blue-50 p-3 rounded">
                    <h4 className="text-sm font-medium text-blue-800 mb-1">Manager Comments</h4>
                    <p className="text-blue-700">{selectedReport.manager_comments}</p>
                  </div>
                )}
                
                {/* Review Actions - Only for managers and unreviewed reports */}
                {canManageProjects && selectedReport.status === "submitted" && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium mb-2">Review Report</h4>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => handleReviewReport(selectedReport.report_id, "reviewed")}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Mark as Reviewed
                      </Button>
                      <Button 
                        onClick={() => handleReviewReport(selectedReport.report_id, "approved")}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Project List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            {isConstructor ? "My Projects" : "Project Management"}
          </h1>
          <p className="text-gray-500">
            {isConstructor ? "View your assigned projects and update task progress" : "Track property development and team tasks"}
          </p>
        </div>
        {canManageProjects && (
          <Button onClick={() => setShowNewProject(true)}>
            <Plus className="w-4 h-4 mr-2" /> New Project
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FolderKanban className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {Object.values(stats.projects || {}).reduce((a, b) => a + b, 0)}
                </p>
                <p className="text-sm text-gray-500">Total Projects</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.projects?.in_progress || 0}</p>
                <p className="text-sm text-gray-500">In Progress</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.overdue_tasks || 0}</p>
                <p className="text-sm text-gray-500">Overdue Tasks</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.tasks?.done || 0}</p>
                <p className="text-sm text-gray-500">Completed Tasks</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Projects Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => (
          <div
            key={project.project_id}
            onClick={() => fetchProjectDetails(project.project_id)}
            className="bg-white rounded-lg border p-5 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold">{project.name}</h3>
                <p className="text-sm text-gray-500">{project.location || "No location"}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}>
                {project.status?.replace("_", " ")}
              </span>
            </div>
            
            {project.description && (
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
            )}
            
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <ListTodo className="w-4 h-4" />
                <span>{project.total_tasks || 0} tasks</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{project.team?.length || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                <span>K{(project.budget || 0).toLocaleString()}</span>
              </div>
            </div>
            
            {project.task_stats && Object.keys(project.task_stats).length > 0 && (
              <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden flex">
                {project.task_stats.done > 0 && (
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${(project.task_stats.done / project.total_tasks) * 100}%` }}
                  />
                )}
                {project.task_stats.in_progress > 0 && (
                  <div
                    className="h-full bg-blue-500"
                    style={{ width: `${(project.task_stats.in_progress / project.total_tasks) * 100}%` }}
                  />
                )}
              </div>
            )}
          </div>
        ))}
        
        {projects.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <FolderKanban className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No projects yet. Create your first project!</p>
          </div>
        )}
      </div>

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="text-sm font-medium">Project Name *</label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="Enter project name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                className="w-full border rounded-md p-2 text-sm"
                rows={3}
                placeholder="Project description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Type</label>
                <select
                  value={newProject.project_type}
                  onChange={(e) => setNewProject({ ...newProject, project_type: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                >
                  <option value="development">Development</option>
                  <option value="renovation">Renovation</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Priority</label>
                <select
                  value={newProject.priority}
                  onChange={(e) => setNewProject({ ...newProject, priority: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Location</label>
              <Input
                value={newProject.location}
                onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                placeholder="Project location"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Date</label>
                <Input
                  type="date"
                  value={newProject.start_date}
                  onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Target End Date</label>
                <Input
                  type="date"
                  value={newProject.target_end_date}
                  onChange={(e) => setNewProject({ ...newProject, target_end_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Budget (K)</label>
              <Input
                type="number"
                value={newProject.budget}
                onChange={(e) => setNewProject({ ...newProject, budget: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowNewProject(false)}>Cancel</Button>
              <Button onClick={handleCreateProject}>Create Project</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectManagement;
